<template>
  <div>
    <appHeader></appHeader>
    <div class="container-fluid recognition-settings">
      <div class="row">
        <div class="col-md-3 col-sm-12 pr-0 dark-bg">
          <appAdminSidebar></appAdminSidebar>
        </div>
        <transition appear name="fade">
          <div class="col-md-9 p-4">
            <!-- Breadcrumb -->
            <nav aria-label="breadcrumb">
              <ol class="breadcrumb">
                <li class="breadcrumb-item">
                  <router-link :to="{ name: 'ManageClients' }">
                    Clients
                  </router-link>
                </li>
                <li class="breadcrumb-item active" aria-current="page">
                  Recognition Settings
                </li>
              </ol>
            </nav>

            <!-- Page Header -->
            <div class="d-flex justify-content-between align-items-center mb-4">
              <div>
                <p class="mb-0">
                  Configure recognition points for <b>{{ clientName }}</b>
                </p>
              </div>
              <div>
                <router-link :to="{ name: 'ManageClients' }">
                  <button type="button" class="btn btn-outline-secondary">
                    <font-awesome-icon icon="arrow-left" class="me-2" />
                    Back to Clients
                  </button>
                </router-link>
              </div>
            </div>

            <!-- Settings Form Card -->
            <div class="card shadow-sm">
              <div class="card-header bg-primary text-white">
                <h5 class="mb-0">
                  <font-awesome-icon icon="cog" class="me-2" />
                  Recognition Configuration
                </h5>
              </div>
              <div class="card-body">
                <form>
                  <!-- Module Enable/Disable -->
                  <div class="row mb-4">
                    <div class="col-md-12">
                      <div class="form-check form-switch">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          id="moduleEnabled"
                          v-model="formData.moduleEnabled"
                        />
                        <label class="form-check-label fw-bold" for="moduleEnabled">
                          Enable Recognition Module
                        </label>
                        <small class="d-block text-muted mt-1">
                          Turn on to activate the recognition points system for this client
                        </small>
                      </div>
                    </div>
                  </div>

                  <!-- Recognition as Kudos Point -->
                  <div class="row mb-4" v-if="formData.moduleEnabled">
                    <div class="col-md-12">
                      <div class="form-check">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          id="kudosPointEnabled"
                          v-model="formData.kudosPointEnabled"
                        />
                        <label class="form-check-label fw-bold" for="kudosPointEnabled">
                          Recognition as Kudos Points (Non-Redeemable)
                        </label>
                        <small class="d-block text-muted mt-1">
                          When enabled, recognition points will be non-redeemable kudos points for appreciation only
                        </small>
                      </div>
                    </div>
                  </div>
                  <hr class="my-4" />

                  <!-- Monthly Givable Points -->
                  <div class="row mb-4">
                    <div class="col-md-6">
                      <label for="monthlyPoints" class="form-label fw-semibold">
                        Monthly Givable Recognition Points per Employee
                        <span class="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        class="form-control"
                        id="monthlyPoints"
                        v-model.number="formData.monthlyGivablePoints"
                        placeholder="Enter monthly points"
                        min="0"
                      />
                      <small class="text-muted">
                        Number of points each employee can give away monthly
                      </small>
                    </div>

                    <div class="col-md-6">
                      <label class="form-label fw-semibold d-block">
                        Points Distribution
                      </label>
                      <div class="info-box">
                        <div class="d-flex align-items-center mb-2">
                          <font-awesome-icon icon="info-circle" class="text-info me-2" />
                          <span class="fw-semibold">How it works:</span>
                        </div>
                        <p class="mb-0 small">
                          If set to 100 points, each employee will receive 100 points at the beginning of each month to recognize their peers.
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Max Points Per Month Challenge -->
                  <div class="row mb-4">
                    <div class="col-md-6">
                      <label for="maxChallengePoints" class="form-label fw-semibold">
                        Max Per Month Point Earn
                        <span class="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        class="form-control"
                        id="maxChallengePoints"
                        v-model.number="formData.maxPerMonthChallengePoints"
                        placeholder="Enter max challenge points"
                        min="0"
                      />
                      <small class="text-muted">
                        Maximum points an employee can earn from recognition per month
                      </small>
                    </div>

                    <div class="col-md-6">
                      <label class="form-label fw-semibold d-block">
                        Limits
                      </label>
                      <div class="info-box">
                        <div class="d-flex align-items-center mb-2">
                          <font-awesome-icon icon="trophy" class="text-warning me-2" />
                          <span class="fw-semibold">Points:</span>
                        </div>
                        <p class="mb-0 small">
                          Sets a monthly cap on points earned through recognition and special recognitions. Helps maintain balanced recognition distribution.
                        </p>
                      </div>
                    </div>
                  </div>

                  <!-- Configuration Status Section -->
                  <div class="row mb-4">
                    <div class="col-md-12">
                      <div class="card border-left-primary">
                        <div class="card-body">
                          <h6 class="card-title text-primary mb-3">
                            <font-awesome-icon icon="chart-line" class="me-2" />
                            Current Configuration Status
                          </h6>
                          <div class="row">
                            <div class="col-md-3">
                              <div class="stat-item">
                                <div class="stat-label">Module Status</div>
                                <div class="stat-value">
                                  <span 
                                    class="badge" 
                                    :class="formData.moduleEnabled ? 'bg-success' : 'bg-warning'"
                                  >
                                    {{ formData.moduleEnabled ? 'Enabled' : 'Disabled' }}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div class="col-md-3">
                              <div class="stat-item">
                                <div class="stat-label">Points Type</div>
                                <div class="stat-value">
                                  <span 
                                    class="badge" 
                                    :class="formData.kudosPointEnabled ? 'bg-info' : 'bg-primary'"
                                  >
                                    {{ formData.kudosPointEnabled ? 'Kudos' : 'Redeemable' }}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div class="col-md-3">
                              <div class="stat-item">
                                <div class="stat-label">Monthly Points Allotment</div>
                                <div class="stat-value text-primary">
                                  {{ formData.monthlyGivablePoints || 0 }} points
                                </div>
                              </div>
                            </div>
                            <div class="col-md-3">
                              <div class="stat-item">
                                <div class="stat-label">Points Cap</div>
                                <div class="stat-value text-primary">
                                  {{ formData.maxPerMonthChallengePoints || 0 }} points
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>


                  <!-- Integration Configuration -->
                  <div class="row mb-4" v-if="formData.createdAt">
                    <div class="col-md-12">
                      <div class="card bg-light">
                        <div class="card-body">
                          <div class="row">
                            <div class="col-md-6">
                                <div class="">
                                    <label class="form-label fw-semibold">Created At</label>
                                    <input
                                    type="text"
                                    class="form-control"
                                    :value="formData.createdAt"
                                    disabled
                                    />
                                </div>
                            </div>
                            <div class="col-md-6">
                              
                              <div class="">
                                <label class="form-label fw-semibold">Last Updated</label>
                                <input
                                  type="text"
                                  class="form-control"
                                  :value="formData.updatedAt"
                                  disabled
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Action Buttons -->
                  <div class="row">
                    <div class="col-md-12">
                      <hr class="mb-4" />
                      <div class="d-flex justify-content-end gap-2">
                        <button 
                          type="button" 
                          class="btn btn-success"
                          @click="saveSettings"
                          :disabled="loading"
                        >
                          <font-awesome-icon v-if="loading" icon="spinner" spin class="me-2" />
                          <font-awesome-icon v-else icon="save" class="me-2" />
                          {{ loading ? 'Saving...' : 'Save Settings' }}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>

            <!-- Loading Spinner -->
            <div v-if="loading" class="centered-spinner">
              <a-spin />
            </div>
          </div>
        </transition>
      </div>
    </div>
    
    <!-- Toast Notification Component -->
    <ToastNotification
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      :duration="3000"
      @close="toast.show = false"
    />
  </div>
</template>

<script>
import Header from "@/components/Header.vue";
import divvyAdminSidebar from "@/views/divvyadmin/divvyAdminSidebar.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import RecognitionSettingsService from "../../../../services/RecognitionSettingsService";

export default {
  name: "ClientRecognitionSettings",
  components: {
    appHeader: Header,
    appAdminSidebar: divvyAdminSidebar,
    ToastNotification,
  },
  data() {
    return {
      loading: false,
      clientId: null,
      clientName: "",
      toast: {
        show: false,
        message: "",
        type: "info"
      },
      formData: {
        moduleEnabled: false,
        kudosPointEnabled: false,
        monthlyGivablePoints: 100,
        maxPerMonthChallengePoints: 500,
        createdAt: null,
        updatedAt: null,
      },
      originalFormData: null,
    };
  },
  methods: {
    setActiveMenuItem() {
      this.$store.dispatch("divvyAdminSidebar/setActive", {
        activeMenuItem: "manageClients",
        activeMenu: "clientusermanagement",
      });
    },
    
    loadSettings() {
      this.loading = true;
      RecognitionSettingsService.getSettings(this.clientId)
        .then((response) => {
          this.loading = false;
          if (response.data.success && response.data.data) {
            const data = response.data.data;
            this.formData = {
              moduleEnabled: data.module_enabled,
              kudosPointEnabled: data.recognition_as_kudos,
              monthlyGivablePoints: data.monthly_givable_recognition_points_per_employee_per_month,
              maxPerMonthChallengePoints: data.max_per_month_challenge_point_earn,
              createdAt: this.formatDateTime(data.created_at),
              updatedAt: this.formatDateTime(data.updated_at),
            };
            this.originalFormData = { ...this.formData };
          } else {
            this.originalFormData = { ...this.formData };
          }
        })
        .catch((e) => {
          this.loading = false;
          console.error("Error loading settings:", e);
          this.originalFormData = { ...this.formData };
          this.showToast("Error loading settings", "error");
        });
    },
    
    saveSettings() {
      // Validate required fields
      if (!this.formData.monthlyGivablePoints || this.formData.monthlyGivablePoints < 0) {
        this.showToast("Please enter a valid monthly points value", "error");
        return;
      }
      
      if (!this.formData.maxPerMonthChallengePoints || this.formData.maxPerMonthChallengePoints < 0) {
        this.showToast("Please enter a valid max points value", "error");
        return;
      }

      this.loading = true;
      
      const payload = {
        client_id: this.clientId,
        module_enabled: this.formData.moduleEnabled,
        recognition_as_kudos: this.formData.kudosPointEnabled,
        monthly_givable_recognition_points_per_employee_per_month: this.formData.monthlyGivablePoints,
        max_per_month_challenge_point_earn: this.formData.maxPerMonthChallengePoints,
      };

      RecognitionSettingsService.saveSettings(payload)
        .then((response) => {
          this.loading = false;
          if (response.data.success) {
            this.showToast(response.data.message || "Settings saved successfully", "success");
            this.loadSettings();
          } else {
            this.showToast(response.data.message || "Failed to save settings", "error");
          }
        })
        .catch((e) => {
          this.loading = false;
          console.error("Error saving settings:", e);
          this.showToast("Error saving settings. Please try again.", "error");
        });
    },
    
    resetForm() {
      if (this.originalFormData) {
        this.formData = { ...this.originalFormData };
      }
    },
    
    formatDateTime(datetime) {
      if (!datetime) return null;
      try {
        const date = new Date(datetime);
        return date.toLocaleString('en-US', { 
          year: 'numeric', 
          month: '2-digit', 
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (e) {
        return datetime;
      }
    },
    
    showToast(message, type = "info") {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;
    },
  },
  created() {
    this.setActiveMenuItem();
    this.clientId = this.$route.params.clientId;
    this.clientName = this.$route.params.clientName || "Client";
    this.loadSettings();
  },
};
</script>

<style lang="scss" scoped>
.recognition-settings {
  min-height: 100%;
  margin: -14px 0 0 0;
}

.breadcrumb {
  background-color: transparent;
  padding: 0;
  margin-bottom: 1rem;
}

.card {
  border: 1px solid #e3e6f0;
  
  &.shadow-sm {
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  }
  
  &.border-left-primary {
    border-left: 4px solid #4e73df;
  }
}

.card-header {
  &.bg-primary {
    background-color: #4e73df !important;
  }
}

.info-box {
  background-color: #f8f9fc;
  border-left: 4px solid #4e73df;
  padding: 1rem;
  border-radius: 0.25rem;
}

.stat-item {
  .stat-label {
    font-size: 0.875rem;
    color: #858796;
    margin-bottom: 0.25rem;
  }
  
  .stat-value {
    font-size: 1.25rem;
    font-weight: 600;
    color: #5a5c69;
  }
}

.fw-semibold {
  font-weight: 600;
}

.gap-2 {
  gap: 0.5rem;
}

.form-control:disabled {
  background-color: #f8f9fc;
  cursor: not-allowed;
}

.centered-spinner {
  position: fixed;
  top: 50%;
  left: 62%;
  transform: translate(-50%, -50%);
  z-index: 9999;
}

.text-primary {
  color: #4e73df !important;
}

.bg-success {
  background-color: #1cc88a !important;
}

.bg-warning {
  background-color: #f6c23e !important;
}

.bg-info {
  background-color: #36b9cc !important;
}

.bg-primary {
  background-color: #4e73df !important;
}

.btn-outline-secondary {
  color: #858796;
  border-color: #858796;
  
  &:hover {
    background-color: #858796;
    color: white;
  }
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>